import { Link } from "react-router-dom";
import { Check, Sparkles } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { trackClick, tutoringUrl } from "../lib/api";

// The app is free for everyone. This page keeps the /pricing URL live so
// any inbound link or SEO backlink still lands somewhere useful — but the
// message is now "no cost, just sign up" rather than plan-picking.

const INCLUDED = [
    "36 lessons across all 5 pillars of the Science of Reading",
    "6 reinforcement games (unlimited plays)",
    "The AI tutor, patient and plainspoken",
    "Text-to-speech on every lesson and passage",
    "Read-aloud practice with instant pronunciation feedback",
    "Weekly progress email to your inbox",
    "Streak saves so a missed day never resets your progress",
];

export default function Pricing() {
    const { user } = useAuth();

    return (
        <main className="container-page py-16 fade-up" data-testid="pricing-page">
            <section className="max-w-3xl mx-auto text-center mb-14">
                <span
                    className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E0EDE6] text-[#1F4237] text-xs font-semibold uppercase tracking-wider mb-5"
                    data-testid="pricing-free-badge"
                >
                    <Sparkles className="w-3.5 h-3.5" strokeWidth={2} /> Now free for everyone
                </span>
                <h1
                    className="text-5xl sm:text-6xl font-semibold text-[#1C2321] mb-5 leading-tight"
                    style={{ fontFamily: "Outfit" }}
                    data-testid="pricing-headline"
                >
                    Lift Up Reading is free.
                </h1>
                <p className="text-xl text-[#515B57] leading-relaxed max-w-2xl mx-auto">
                    No card, no trial ticker, no subscription page to cancel. Sign up, take the placement
                    check, and start your first lesson. That's it.
                </p>
            </section>

            <section
                className="max-w-3xl mx-auto card-rw p-8 sm:p-10 mb-12"
                data-testid="pricing-free-card"
            >
                <div className="flex items-baseline justify-center gap-2 mb-2">
                    <span
                        className="text-6xl font-semibold text-[#2D5D4E]"
                        style={{ fontFamily: "Outfit" }}
                    >
                        $0
                    </span>
                    <span className="text-[#819888] text-lg">forever</span>
                </div>
                <p className="text-center text-[#515B57] mb-8">
                    Everything below is included from the moment you sign up. No caps, no locked lessons,
                    no premium tier hidden behind a paywall.
                </p>

                <ul className="space-y-3 mb-8 max-w-xl mx-auto" data-testid="pricing-included-list">
                    {INCLUDED.map((line) => (
                        <li key={line} className="flex items-start gap-3">
                            <Check
                                className="w-5 h-5 text-[#2D5D4E] flex-shrink-0 mt-0.5"
                                strokeWidth={2}
                            />
                            <span className="text-[#1C2321]">{line}</span>
                        </li>
                    ))}
                </ul>

                <div className="text-center">
                    {user ? (
                        <Link
                            to="/app"
                            data-testid="pricing-cta-app"
                            className="inline-flex items-center gap-2 px-8 py-4 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] text-lg font-medium hover:bg-[#1F4237] transition-colors"
                        >
                            Go to your dashboard
                        </Link>
                    ) : (
                        <Link
                            to="/register"
                            onClick={() => trackClick({ surface: "pricing", target: "signup" })}
                            data-testid="pricing-cta-register"
                            className="inline-flex items-center gap-2 px-8 py-4 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] text-lg font-medium hover:bg-[#1F4237] transition-colors"
                        >
                            Sign up — it's free
                        </Link>
                    )}
                    <p className="text-sm text-[#819888] mt-3">
                        Already have an account?{" "}
                        <Link to="/login" className="underline text-[#2D5D4E]">
                            Sign in
                        </Link>
                    </p>
                </div>
            </section>

            <section className="max-w-3xl mx-auto text-center" data-testid="pricing-tutoring-cta">
                <p className="label-eyebrow mb-2">One-on-one tutoring</p>
                <h2
                    className="text-3xl font-semibold text-[#1C2321] mb-3"
                    style={{ fontFamily: "Outfit" }}
                >
                    Want a real person in your corner?
                </h2>
                <p className="text-[#515B57] mb-5 max-w-xl mx-auto">
                    The app is free. If you'd like private tutoring sessions with a certified adult-literacy
                    coach, we offer that separately.
                </p>
                <a
                    href={tutoringUrl()}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => trackClick({ surface: "pricing", target: "tutoring" })}
                    data-testid="pricing-tutoring-link"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-[#2D5D4E] text-[#2D5D4E] font-medium hover:bg-[#E0EDE6] transition-colors"
                >
                    Learn about tutoring
                </a>
            </section>
        </main>
    );
}
